// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { initializeApp, type FirebaseApp } from 'firebase/app'
import {
  getAuth,
  signInWithCustomToken as fbSignInWithCustomToken,
  type Auth,
  type UserCredential,
} from 'firebase/auth'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  tenantId: import.meta.env.VITE_FIREBASE_TENANT_ID,
}

// Surface misconfiguration early rather than failing deep inside the SDK.
export const isFirebaseConfigured = Boolean(
  firebaseConfig.apiKey &&
    firebaseConfig.authDomain &&
    firebaseConfig.projectId &&
    firebaseConfig.appId &&
    firebaseConfig.tenantId,
)

if (!isFirebaseConfigured) {
  console.error(
    'Firebase is not configured. Set VITE_FIREBASE_API_KEY, VITE_FIREBASE_AUTH_DOMAIN, ' +
      'VITE_FIREBASE_PROJECT_ID, VITE_FIREBASE_APP_ID and VITE_FIREBASE_TENANT_ID in apps/patient-web/.env',
  )
}

// Initialize behind a guard so a misconfigured deploy degrades to a handled "sign-in
// unavailable" screen (gated on isFirebaseConfigured) instead of white-screening the app at
// import time. Screens must check isFirebaseConfigured before touching `auth`.
let authInstance: Auth | undefined
if (isFirebaseConfigured) {
  try {
    const app: FirebaseApp = initializeApp(firebaseConfig)
    authInstance = getAuth(app)
    if (firebaseConfig.tenantId) authInstance.tenantId = firebaseConfig.tenantId
  } catch (err) {
    console.error('Firebase initialization failed', err)
  }
}

// True only when auth actually initialized — env vars can be present but malformed and make
// initializeApp/getAuth throw, leaving `auth` undefined. Guards must gate on this, not on the
// env-only isFirebaseConfigured, so the degrade screen shows in that case too.
export const isFirebaseReady = Boolean(authInstance)

export const auth = authInstance as Auth

// Trade a backend-minted qh-patients tenant custom token for a Firebase session. The token
// is tenant-scoped and honored against the pinned auth.tenantId; on success the patient holds
// an authenticated tenant session to enroll durable login providers (phone, Google). Throws
// if Firebase never initialized so callers surface a handled error instead of a raw SDK crash.
export function signInWithCustomToken(token: string): Promise<UserCredential> {
  if (!authInstance) return Promise.reject(new Error('firebase-not-ready'))
  return fbSignInWithCustomToken(authInstance, token)
}
